import { setMusicBusEnabled, whenAudioReady, type AudioGraph } from "./engine.ts";
import { bass, bell, keys, marimba, noise, pad, pluck } from "./instruments.ts";

/**
 * Background music, composed as data and played by the synthesized
 * instruments: chord changes, a written melody, then a variation, then a
 * quieter "breath" section, so the loop does not wear thin. A look-ahead
 * scheduler queues notes ~0.2 s ahead on the AudioContext clock.
 */
export type TrackId = "town" | "focus";

interface MelodyNote {
  /** Beat offset inside the bar. */
  readonly beat: number;
  readonly midi: number;
  readonly beats: number;
}

interface Section {
  /** Melody per bar (8 bars); empty arrays are rests. */
  readonly melody: readonly (readonly MelodyNote[])[] | null;
  readonly voice: "bell" | "pluck";
  /** Random ornament notes per bar in sections without a melody. */
  readonly sparkle?: number;
}

interface Song {
  readonly bpm: number;
  /** Eight bars: chord voicing (3–4 notes) and bass root. */
  readonly chords: readonly { readonly notes: readonly number[]; readonly root: number }[];
  readonly sections: readonly Section[];
  readonly style: "town" | "focus";
  readonly scale: readonly number[];
}

const N = (beat: number, midi: number, beats: number): MelodyNote => ({ beat, midi, beats });

const TOWN: Song = {
  bpm: 84,
  style: "town",
  scale: [72, 74, 77, 79, 81, 84, 86],
  chords: [
    { notes: [53, 57, 60, 64], root: 41 }, // Fmaj7
    { notes: [50, 53, 57, 60], root: 38 }, // Dm7
    { notes: [55, 58, 62, 65], root: 43 }, // Gm7
    { notes: [52, 55, 58, 60], root: 36 }, // C7
    { notes: [57, 60, 64, 67], root: 45 }, // Am7
    { notes: [50, 53, 57, 60], root: 38 }, // Dm7
    { notes: [58, 62, 65, 69], root: 46 }, // Bbmaj7
    { notes: [60, 64, 67, 69], root: 36 }, // C6
  ],
  sections: [
    {
      voice: "bell",
      melody: [
        [N(0, 69, 1), N(1, 72, 1), N(2, 76, 1.5), N(3.5, 74, 0.5)],
        [N(0, 72, 1), N(1, 69, 1), N(2, 65, 2)],
        [N(0, 67, 1), N(1, 70, 1), N(2, 74, 1), N(3, 77, 1)],
        [N(0, 76, 2), N(3, 67, 1)],
        [N(0, 69, 1), N(1, 72, 1), N(2, 76, 1), N(3, 79, 1)],
        [N(0, 77, 1.5), N(1.5, 76, 0.5), N(2, 74, 1), N(3, 69, 1)],
        [N(0, 70, 1), N(1, 74, 1), N(2, 77, 1), N(3, 81, 1)],
        [N(0, 79, 2), N(2, 76, 1), N(3, 72, 1)],
      ],
    },
    {
      voice: "pluck",
      melody: [
        [N(0, 84, 2), N(2, 81, 2)],
        [N(0, 77, 4)],
        [N(0, 74, 2), N(2, 77, 2)],
        [N(0, 76, 3)],
        [N(0, 76, 2), N(2, 72, 2)],
        [N(0, 74, 2), N(2, 77, 2)],
        [N(0, 77, 2), N(2, 74, 1), N(3, 77, 1)],
        [N(0, 76, 4)],
      ],
    },
    { voice: "bell", melody: null, sparkle: 1 },
  ],
};

const FOCUS: Song = {
  bpm: 68,
  style: "focus",
  scale: [74, 76, 78, 81, 83, 86, 88],
  chords: [
    { notes: [54, 57, 61, 64], root: 38 }, // Dmaj9
    { notes: [54, 57, 59, 62], root: 47 }, // Bm7
    { notes: [54, 59, 62, 67], root: 43 }, // Gmaj7
    { notes: [57, 62, 64, 66], root: 45 }, // A6sus
    { notes: [57, 61, 64, 66], root: 42 }, // F#m7
    { notes: [54, 57, 59, 62], root: 47 }, // Bm7
    { notes: [55, 59, 62, 64], root: 40 }, // Em7
    { notes: [57, 61, 64, 66], root: 45 }, // A6
  ],
  sections: [
    {
      voice: "bell",
      melody: [
        [N(0, 78, 1.5), N(1.5, 81, 0.5), N(2, 76, 2)],
        [N(0, 74, 2), N(3, 78, 1)],
        [N(0, 71, 1), N(1, 74, 1), N(2, 78, 2)],
        [N(0, 76, 3)],
        [N(0, 73, 1.5), N(1.5, 76, 0.5), N(2, 81, 2)],
        [N(0, 78, 2), N(2, 74, 2)],
        [N(0, 76, 1), N(1, 79, 1), N(2, 83, 2)],
        [N(0, 81, 3)],
      ],
    },
    { voice: "bell", melody: null, sparkle: 2 },
  ],
};

const SONGS: Record<TrackId, Song> = { town: TOWN, focus: FOCUS };

interface Playing {
  readonly id: TrackId;
  readonly song: Song;
  readonly transpose: number;
  readonly gain: GainNode;
  step: number;
  nextTime: number;
}

const LOOKAHEAD = 0.22;
const FADE = 1.4;

let enabled = true;
let wanted: { id: TrackId; transpose: number } | null = null;
let playing: Playing | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function scheduleStep(graph: AudioGraph, track: Playing, t: number): void {
  const { song, transpose, gain } = track;
  const beatDur = 60 / song.bpm;
  const barsPerSection = song.chords.length;
  const totalBars = barsPerSection * song.sections.length;
  const bar = Math.floor(track.step / 8) % totalBars;
  const section = song.sections[Math.floor(bar / barsPerSection)];
  const barInSection = bar % barsPerSection;
  const stepInBar = track.step % 8;
  const chord = song.chords[barInSection];
  if (!section || !chord) return;
  const tr = (midi: number) => midi + transpose;
  const quiet = section.melody === null;

  // Chords and bass.
  if (stepInBar === 0) {
    if (song.style === "town") {
      for (const note of chord.notes) pad(graph, gain, tr(note), t, { gain: quiet ? 0.032 : 0.026, duration: beatDur * 4.2 });
      bass(graph, gain, tr(chord.root), t, { gain: 0.13, duration: beatDur * 1.6 });
    } else {
      for (const note of chord.notes) keys(graph, gain, tr(note), t, { gain: 0.04, duration: beatDur * 4 });
      bass(graph, gain, tr(chord.root), t, { gain: 0.1, duration: beatDur * 3.6 });
    }
  }
  if (song.style === "town") {
    if (stepInBar === 4) bass(graph, gain, tr(chord.root + 7), t, { gain: 0.1, duration: beatDur * 1.5 });
    if (stepInBar === 7 && Math.random() < 0.5) bass(graph, gain, tr(chord.root + 12), t, { gain: 0.06, duration: beatDur * 0.5 });
    // Broken-chord marimba on the off-beats.
    if (stepInBar % 2 === 1) {
      const tone = chord.notes[[1, 2, 3, 2][(stepInBar - 1) / 2] ?? 1] ?? chord.notes[0] ?? 60;
      marimba(graph, gain, tr(tone + 12), t, { gain: quiet ? 0.07 : 0.05, duration: 0.4, pan: stepInBar % 4 === 1 ? -0.3 : 0.3 });
    }
    // Soft shaker.
    noise(graph, gain, t, { from: 8000, duration: 0.05, gain: stepInBar % 2 === 1 ? 0.012 : 0.006, type: "highpass", space: 0 });
  } else if (stepInBar === 5 && Math.random() < 0.45) {
    keys(graph, gain, tr((chord.notes[3] ?? 64) + 12), t, { gain: 0.025, duration: beatDur * 2 });
  }

  // Melody notes that start inside this eighth.
  const beat = stepInBar / 2;
  const notes = section.melody?.[barInSection] ?? [];
  for (const note of notes) {
    if (note.beat < beat || note.beat >= beat + 0.5) continue;
    const at = t + (note.beat - beat) * beatDur;
    const duration = Math.max(0.6, note.beats * beatDur * 1.3);
    if (section.voice === "bell") bell(graph, gain, tr(note.midi), at, { gain: 0.07, duration });
    else pluck(graph, gain, tr(note.midi), at, { gain: 0.09, duration });
  }
  if (section.sparkle && stepInBar % 4 === 0 && Math.random() < section.sparkle * 0.22) {
    const note = song.scale[Math.floor(Math.random() * song.scale.length)] ?? 84;
    bell(graph, gain, tr(note + (Math.random() < 0.3 ? 12 : 0)), t, { gain: 0.035, duration: 1.8, pan: Math.random() - 0.5 });
  }
}

function tick(graph: AudioGraph): void {
  const track = playing;
  if (!track) return;
  const stepDur = 60 / track.song.bpm / 2;
  // After a long pause (tab hidden), resync instead of bursting notes.
  if (track.nextTime < graph.ctx.currentTime - 0.5) track.nextTime = graph.ctx.currentTime + 0.05;
  while (track.nextTime < graph.ctx.currentTime + LOOKAHEAD) {
    scheduleStep(graph, track, track.nextTime);
    track.step += 1;
    track.nextTime += stepDur;
  }
}

function fadeOut(graph: AudioGraph, track: Playing): void {
  const now = graph.ctx.currentTime;
  track.gain.gain.cancelScheduledValues(now);
  track.gain.gain.setValueAtTime(track.gain.gain.value, now);
  track.gain.gain.linearRampToValueAtTime(0, now + FADE);
  setTimeout(() => track.gain.disconnect(), (FADE + 4) * 1000);
}

function start(graph: AudioGraph): void {
  if (enabled && playing && wanted && playing.id === wanted.id && playing.transpose === wanted.transpose) return;
  if (playing) fadeOut(graph, playing);
  playing = null;
  if (!wanted || !enabled) {
    if (timer) clearInterval(timer);
    timer = null;
    return;
  }
  const gain = graph.ctx.createGain();
  const now = graph.ctx.currentTime;
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(1, now + FADE);
  gain.connect(graph.music);
  playing = { id: wanted.id, song: SONGS[wanted.id], transpose: wanted.transpose, gain, step: 0, nextTime: now + 0.1 };
  timer ??= setInterval(() => tick(graph), 40);
}

/** Selects the background track; null fades to silence. */
export function setMusicTrack(id: TrackId | null, transpose = 0): void {
  wanted = id ? { id, transpose } : null;
  whenAudioReady(start);
}

export function setMusicEnabled(value: boolean): void {
  enabled = value;
  setMusicBusEnabled(value);
  whenAudioReady(start);
}
